import {
  getAuthApiUrl,
  getStoredSessionToken,
  isSessionExpiredPayload,
  isSessionRefreshRequiredPayload,
} from '../../../app/services/session.service';

const WORKSPACE_SETTINGS_API_PATH = '/api/workspace/settings';

function buildResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function getWorkspaceSettingsApiUrl(path = WORKSPACE_SETTINGS_API_PATH) {
  return getAuthApiUrl(path);
}

function getWorkspaceSettingsAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function mapWorkspaceSettings(payload) {
  const settings = payload?.settings && typeof payload.settings === 'object' ? payload.settings : {};

  return {
    tenantId: payload?.tenantId || '',
    name: payload?.name || '',
    slug: payload?.slug || '',
    active: payload?.active !== false,
    planCode: payload?.planCode || '',
    enabledModuleKeys: Array.isArray(payload?.enabledModuleKeys) ? payload.enabledModuleKeys : [],
    settings: {
      displayName: settings.displayName || '',
      legalName: settings.legalName || '',
      contactEmail: settings.contactEmail || '',
      contactPhone: settings.contactPhone || '',
      website: settings.website || '',
      timezone: settings.timezone || '',
      locale: settings.locale || '',
      defaultCurrency: settings.defaultCurrency || '',
      countryCode: settings.countryCode || '',
      addressLine1: settings.addressLine1 || '',
      addressLine2: settings.addressLine2 || '',
      city: settings.city || '',
      postalCode: settings.postalCode || '',
    },
    createdAt: payload?.createdAt || null,
    updatedAt: payload?.updatedAt || null,
  };
}

async function parseJson(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function parseErrorMessage(payload, fallbackMessage) {
  if (payload && typeof payload.message === 'string') {
    return payload.message;
  }

  if (payload && typeof payload.error === 'string') {
    return payload.error;
  }

  return fallbackMessage;
}

function buildAuthFailureResult(payload, fallbackMessage) {
  if (isSessionRefreshRequiredPayload(payload)) {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_SETTINGS_SESSION_REFRESH_REQUIRED',
      message: parseErrorMessage(payload, 'Sessione non aggiornata. Effettua di nuovo l\'accesso.'),
    });
  }

  if (isSessionExpiredPayload(payload)) {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_SETTINGS_SESSION_EXPIRED',
      message: parseErrorMessage(payload, 'Sessione scaduta. Effettua di nuovo il login.'),
    });
  }

  return buildResult({
    ok: false,
    error: 'WORKSPACE_SETTINGS_AUTH_REQUIRED',
    message: fallbackMessage,
  });
}

export async function getWorkspaceSettings() {
  try {
    const response = await fetch(getWorkspaceSettingsApiUrl(), {
      headers: getWorkspaceSettingsAuthHeaders(),
    });
    const payload = await parseJson(response);

    if (response.status === 401 || response.status === 403) {
      return buildAuthFailureResult(payload, 'Sessione non autorizzata per leggere le impostazioni workspace.');
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_SETTINGS_GET_FAILED_${response.status}`);
    }

    return buildResult({
      ok: true,
      data: {
        workspace: mapWorkspaceSettings(payload),
      },
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_SETTINGS_UNAVAILABLE',
      message: 'Impostazioni workspace non disponibili.',
    });
  }
}

export async function updateWorkspaceSettings(payload) {
  try {
    const response = await fetch(getWorkspaceSettingsApiUrl(), {
      method: 'PATCH',
      headers: getWorkspaceSettingsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    const responsePayload = await parseJson(response);

    if (response.status === 401 || response.status === 403) {
      return buildAuthFailureResult(responsePayload, 'Sessione non autorizzata per modificare le impostazioni workspace.');
    }

    if (response.status === 400) {
      return buildResult({
        ok: false,
        error: 'WORKSPACE_SETTINGS_VALIDATION_ERROR',
        message: parseErrorMessage(responsePayload, 'Impostazioni workspace non valide.'),
      });
    }

    if (!response.ok) {
      throw new Error(`WORKSPACE_SETTINGS_UPDATE_FAILED_${response.status}`);
    }

    return buildResult({
      ok: true,
      data: {
        workspace: mapWorkspaceSettings(responsePayload),
      },
      message: 'Impostazioni workspace aggiornate.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'WORKSPACE_SETTINGS_UPDATE_UNAVAILABLE',
      message: 'Aggiornamento impostazioni workspace non completato.',
    });
  }
}
